import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, MenuItem, Stack, TextField } from "@mui/material";

import {
	useAssignUserRolesMutation,
	useGetRolesQuery,
	useUnAssignUserRolesMutation,
	useUpdateUserMutation,
} from "@/pages/Private/redux/admin/admin.api";
import { Loader, Modal } from "@/components";
import { Toggle } from "@/components/Toggle/Toggle";
import {
	ERROR_TYPE,
	getAllUserRoles,
	getUserRolesDiff,
	getAllErrors,
	renderErrorMessages,
} from "@/utils";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { refusalText } from "@/utils/refusalText";
import { PermissionRoles } from "@/enum";
import { User } from "@/pages/Public/pages/Login/schema/login";
import { DEFAULT, SUPPORT_COMPANY_ID } from "@/constants";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { UpdateUser, UpdateUserForm, UpdateUserFormSchema } from "../schema/update-user";
import { heldStaffRoleNote, heldStaffRolesNotOffered, rolesOfferedFor } from "./grantableRoles";

interface EditUserModalProps {
	user: User | null;
	isVisible: boolean;
	handleClose: () => void;
	companyId?: number;
}

export const EditUserModal: FunctionComponent<EditUserModalProps> = ({
	user,
	isVisible,
	handleClose,
	companyId,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`modals.editUser.${key}`);

	const { hasPermissions } = useHasPermissions();

	const [selectedUser, setSelectedUser] = useState<User | null>(user);
	const [isSupport, setIsSupport] = useState<boolean>(user?.isSupport ?? false);

	// The support flag is editable only by a global admin and only for users of
	// the internal company (mirrors the backend authorization rule).
	const canEditSupport =
		hasPermissions([PermissionRoles.GLOBAL_ADMIN]) && companyId === SUPPORT_COMPANY_ID;

	const { data: roles } = useGetRolesQuery();
	const [updateUser, { isLoading }] = useUpdateUserMutation();
	/** Why the last save was refused (user update or role change), shown under the form (PREC-4636). */
	const [saveError, setSaveError] = useState<unknown>(null);
	const [assignRole, { isLoading: isAssignRoleLoading }] = useAssignUserRolesMutation();
	const [unAssignRole, { isLoading: isUnassignRoleLoading }] = useUnAssignUserRolesMutation();

	const initialValues: UpdateUserForm = {
		firstName: selectedUser?.firstName ?? "",
		lastName: selectedUser?.lastName ?? "",
		email: selectedUser?.email ?? "",
		roles: getAllUserRoles(selectedUser?.roles || []) ?? [],
	};

	const {
		control,
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<UpdateUserForm>({
		defaultValues: initialValues,
		resolver: zodResolver(UpdateUserFormSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const {
		fields: userRoles,
		remove,
		append,
	} = useFieldArray({
		control,
		name: "roles",
	});

	const onSubmit = async (values: UpdateUserForm) => {
		const body = {} as UpdateUser;
		const { roles: selectedRoles, ...info } = values;

		for (const key in info) {
			const initialValue = initialValues[key as keyof UpdateUser];
			const currentValue = values[key as keyof UpdateUser];

			if (currentValue !== initialValue) {
				body[key as keyof UpdateUser] = currentValue;
			}
		}

		const supportChanged = canEditSupport && isSupport !== (selectedUser?.isSupport ?? false);
		const finalBody = supportChanged ? { ...body, isSupport } : body;

		setSaveError(null);

		try {
			if (Object.entries(finalBody).length > 0) {
				await updateUser({ id: selectedUser?.id ?? 0, body: finalBody }).unwrap();
			}

			if (selectedUser && roles) {
				const { addedRoleIds, removedRoleIds } = getUserRolesDiff(
					selectedUser.roles.map((role) => role.code),
					selectedRoles?.map((role) => `${role.identifier}`) ?? [],
					roles.data
				);

				if (addedRoleIds.length) {
					await assignRole({
						id: selectedUser.id,
						body: { roles: addedRoleIds },
					}).unwrap();
				}

				if (removedRoleIds.length) {
					await unAssignRole({
						id: selectedUser.id,
						body: { roles: removedRoleIds },
					}).unwrap();
				}
			}

			handleClose();
		} catch (err) {
			// A refused update or role change keeps the modal open and says why (QA comment 27620:
			// a refused role change used to be logged only, so the modal sat there saying nothing).
			setSaveError(err);
		}
	};

	const handleUnselectRole = (code: string) => {
		const indexOfRemovedRole = userRoles.findIndex((role) => role.identifier === code);

		remove(indexOfRemovedRole);
	};

	const handleUserRoleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const selectedRole = roles?.data.find((role) => role.code === event.target.value);

		if (selectedRole) {
			remove();
			append(getAllUserRoles([selectedRole]));
		}
	};

	// The roles the API would accept from this caller for this user: ADMIN (GLOBAL_ADMIN) only for a
	// confirmed staff-domain (@signalist.io, @digital-now.io, @signalist.de) address (PREC-4636), nothing above the caller's own
	// permissions (PREC-4634). A staff role the user already holds but may not be granted stays
	// visible, selected and disabled, so staff can see it and switch the user off it; the diff below
	// maps through the full list, so that removal goes through.
	const callerRoles = useAppSelector(authSelector).user?.roles;
	const grantee = useMemo(
		() => ({
			email: selectedUser?.email,
			emailVerifiedAt: selectedUser?.emailVerifiedAt,
			invitedAt: selectedUser?.invitedAt,
		}),
		[selectedUser?.email, selectedUser?.emailVerifiedAt, selectedUser?.invitedAt]
	);
	const rolesOptions = useMemo(
		() => rolesOfferedFor(roles?.data ?? [], grantee, callerRoles ?? []),
		[roles?.data, grantee, callerRoles]
	);
	const heldNotOffered = useMemo(
		() => heldStaffRolesNotOffered(selectedUser?.roles ?? [], rolesOptions),
		[selectedUser?.roles, rolesOptions]
	);
	const heldNote = heldStaffRoleNote(grantee);

	useEffect(() => {
		if (user) {
			setSelectedUser(user);
			setIsSupport(user.isSupport ?? false);
		}
	}, [user]);

	return (
		<Modal
			handleClose={handleClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading || isAssignRoleLoading || isUnassignRoleLoading}
			isOpened={isVisible}
			title={ts("title")}
		>
			{!roles ? (
				<Loader />
			) : (
				<>
					<Stack spacing={2}>
						<TextField
							error={!!errors.email?.message}
							inputProps={{ readOnly: true }}
							label={t("basics.email")}
							{...register("email")}
						/>

						<TextField
							error={!!errors.firstName?.message}
							label={t("basics.firstName")}
							{...register("firstName")}
						/>

						<TextField
							error={!!errors.lastName?.message}
							label={t("basics.lastName")}
							{...register("lastName")}
						/>

						<TextField
							select
							error={!!errors.roles?.message}
							label={t("basics.role")}
							value={userRoles[0]?.identifier ?? DEFAULT}
							onChange={handleUserRoleChange}
						>
							<MenuItem disabled value={DEFAULT}>
								{ts("rolePlaceholder")}
							</MenuItem>
							{rolesOptions.map((role) => (
								<MenuItem key={role.id} value={role.code}>
									{role.name}
								</MenuItem>
							))}
							{heldNotOffered.map((role) => (
								<MenuItem key={`held-${role.id}`} disabled value={role.code}>
									{heldNote ? `${role.name} (${heldNote})` : role.name}
								</MenuItem>
							))}
						</TextField>

						{canEditSupport && (
							<div className="flex items-center gap-3" data-test="edit-user-support-row">
								<Toggle
									isChecked={isSupport}
									testId="edit-user-support-toggle"
									onChange={() => setIsSupport((prev) => !prev)}
								/>
								<span className="text-sm text-gray-700">{ts("supportLabel")}</span>
							</div>
						)}

						{getAllErrors(undefined, formErrors).length
							? renderErrorMessages(getAllErrors(undefined, formErrors))
							: null}

						{saveError ? (
							<Alert
								severity="error"
								sx={{ alignItems: "center", bgcolor: "error.light", fontSize: 14 }}
							>
								{refusalText(saveError)}
							</Alert>
						) : null}
					</Stack>
				</>
			)}
		</Modal>
	);
};
